import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import StaticAdsService from 'services/StaticAdsService';

import FormatEditorDialog from './FormatEditorDialog';
import StructuredGenerateDialog from './StructuredGenerateDialog';
import { aspectLabel } from './staticAspects';
import { StaticFormat } from './types';

// The formats library: the structured half of Static Creative.
//
// A format is a whole ad structure — pick one, give it a product and a brief,
// and it builds an ad in that shape. No source ad and no variation axis: the
// format IS the composition, which is exactly why it stopped being a knob on
// the remake dialog.

/** What the operator is about to do, held until they confirm it. */
type Pending =
  | { kind: 'delete'; format: StaticFormat }
  | { kind: 'reset'; format: StaticFormat }
  | null;

function FormatCard({ format, onGenerate, onEdit, onDuplicate, onDelete, onReset, busy }: {
  format: StaticFormat;
  onGenerate: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onReset: () => void;
  busy: boolean;
}) {
  const isBuiltin = format.source === 'builtin';
  return (
    <Box
      sx={{
        p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1,
        // Custom formats are scannable without reading a chip: the operator's
        // own formats are the ones they will look for.
        borderLeft: isBuiltin ? '1px solid' : '3px solid',
        borderLeftColor: isBuiltin ? 'divider' : 'primary.main',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
        <Typography variant="subtitle2">{format.label}</Typography>
        {!isBuiltin && <Chip size="small" color="primary" variant="outlined" label="custom" sx={{ height: 18, fontSize: '0.62rem' }} />}
        {format.overridden && (
          <Tooltip title="Edited from the built-in default — Reset restores it">
            <Chip size="small" color="warning" variant="outlined" label="edited" sx={{ height: 18, fontSize: '0.62rem' }} />
          </Tooltip>
        )}
        <Chip size="small" variant="outlined" label={aspectLabel(format.defaultAspect)} sx={{ height: 18, fontSize: '0.62rem' }} />
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {format.description}
      </Typography>

      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        <Button size="small" variant="contained" onClick={onGenerate} disabled={busy}>Generate</Button>
        <Button size="small" onClick={onEdit} disabled={busy}>Edit</Button>
        <Button size="small" onClick={onDuplicate} disabled={busy}>Duplicate</Button>
        {isBuiltin
          ? (
            <Tooltip title={format.overridden ? 'Restore the built-in wording' : 'Nothing to reset — this is the built-in wording'}>
              <span>
                <Button size="small" color="warning" onClick={onReset} disabled={busy || !format.overridden}>
                  Reset
                </Button>
              </span>
            </Tooltip>
          )
          : <Button size="small" color="error" onClick={onDelete} disabled={busy}>Delete</Button>}
      </Stack>
    </Box>
  );
}

export default function FormatLibrary() {
  const service = useMemo(() => new StaticAdsService(), []);
  const [formats, setFormats] = useState<StaticFormat[] | null>(null);
  // A failed load must never render as an empty library — the operator would
  // reasonably conclude their formats were gone.
  const [loadError, setLoadError] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [editing, setEditing] = useState<StaticFormat | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [generateFor, setGenerateFor] = useState<StaticFormat | null>(null);
  const [pending, setPending] = useState<Pending>(null);

  const load = useCallback(() => {
    setFormats(null);
    setLoadError(false);
    void service.listStaticFormats().then((resp) => {
      if (resp) setFormats(resp.formats || []);
      else setLoadError(true);
    });
  }, [service]);

  useEffect(() => { load(); }, [load]);

  const replace = (saved: StaticFormat) => {
    setFormats((prev) => {
      if (!prev) return prev;
      return prev.some(f => f.key === saved.key)
        ? prev.map(f => (f.key === saved.key ? saved : f))
        : [...prev, saved];
    });
  };

  const act = async (key: string, fn: () => Promise<void>) => {
    setBusyKey(key);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err?.response?.data?.error || err?.message || 'That did not work');
    } finally {
      setBusyKey(null);
    }
  };

  const duplicate = (format: StaticFormat) => act(format.key, async () => {
    const resp = await service.duplicateStaticFormat(format.key);
    replace(resp.format);
    // Straight into the copy: duplicating is almost always the first half of
    // "make one like this but different".
    setEditing(resp.format);
    setEditorOpen(true);
  });

  const confirmReset = (format: StaticFormat) => act(format.key, async () => {
    const resp = await service.resetStaticFormat(format.key);
    replace(resp.format);
    setPending(null);
  });

  const confirmDelete = (format: StaticFormat) => act(format.key, async () => {
    await service.deleteStaticFormat(format.key);
    setFormats(prev => (prev ? prev.filter(f => f.key !== format.key) : prev));
    setPending(null);
  });

  // Grouped so the library reads as a set of families rather than 15 flat cards.
  const byFamily = useMemo(() => {
    const groups = new Map<string, StaticFormat[]>();
    for (const format of formats || []) {
      const list = groups.get(format.family) || [];
      list.push(format);
      groups.set(format.family, list);
    }
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [formats]);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Build an ad from a known structure. Pick a format, give it a product and a brief — no
            source ad involved. To rebuild an existing ad instead, use the Library.
          </Typography>
        </Box>
        <Button
          size="small" variant="contained"
          onClick={() => { setEditing(null); setEditorOpen(true); }}
        >
          New format
        </Button>
      </Stack>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>
      )}

      {loadError && (
        <Alert
          severity="error" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
        >
          Couldn&apos;t load the formats — the API may be down. This is not the same as having none.
        </Alert>
      )}

      {!loadError && !formats && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {!loadError && formats && !formats.length && (
        <Alert severity="info">No formats yet — create one to get started.</Alert>
      )}

      {!loadError && formats && byFamily.map(([family, items]) => (
        <Box key={family} sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary">{family}</Typography>
          <Box sx={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 1.5, mt: 0.5,
          }}>
            {items.map(format => (
              <FormatCard
                key={format.key}
                format={format}
                busy={busyKey === format.key}
                onGenerate={() => setGenerateFor(format)}
                onEdit={() => { setEditing(format); setEditorOpen(true); }}
                onDuplicate={() => duplicate(format)}
                onReset={() => setPending({ kind: 'reset', format })}
                onDelete={() => setPending({ kind: 'delete', format })}
              />
            ))}
          </Box>
        </Box>
      ))}

      <FormatEditorDialog
        open={editorOpen}
        format={editing}
        service={service}
        onClose={() => setEditorOpen(false)}
        onSaved={replace}
      />

      <StructuredGenerateDialog
        open={!!generateFor}
        format={generateFor}
        service={service}
        onClose={() => setGenerateFor(null)}
      />

      <Dialog open={!!pending} onClose={() => setPending(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {pending?.kind === 'reset' ? 'Reset this format?' : 'Delete this format?'}
        </DialogTitle>
        <DialogContent>
          {pending?.kind === 'reset' ? (
            <DialogContentText component="div">
              <strong>{pending.format.label}</strong> goes back to the built-in wording.
              <Box sx={{ mt: 1 }}>
                The edited wording is <strong>discarded permanently</strong> — there is no way to
                get it back.
              </Box>
            </DialogContentText>
          ) : (
            <DialogContentText component="div">
              <strong>{pending?.format.label}</strong> will stop being offered when generating.
              <Box sx={{ mt: 1 }}>
                Creatives already made with it keep their images and their prompts, and still name it.
              </Box>
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Cancel</Button>
          {pending?.kind === 'reset' ? (
            <Button
              color="warning" variant="contained"
              disabled={!!busyKey}
              onClick={() => pending && confirmReset(pending.format)}
            >
              Reset
            </Button>
          ) : (
            <Button
              color="error" variant="contained"
              disabled={!!busyKey}
              onClick={() => pending && confirmDelete(pending.format)}
            >
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
